import { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

/** Translation proxy root: every segment is `I18nT`; use `String(...)` for the resolved phrase. */
export interface I18nT {
  readonly [key: string]: I18nT
}

function createTranslationProxy(tFn: (key: string) => string, prefix?: string): I18nT {
  return new Proxy({} as I18nT, {
    get(_, prop: string | symbol) {
      // Coercion: must not define these on the target — Proxy invariants require the trap result
      // to match a non-configurable target property; `defineProperty` on leaves caused String(t.x) to throw.
      if (prop === Symbol.toPrimitive || prop === 'valueOf' || prop === 'toString') {
        return () => (prefix ? tFn(prefix) : '')
      }
      if (typeof prop !== 'string') {
        return undefined as unknown as I18nT
      }
      const key = prefix ? `${prefix}.${prop}` : prop
      return createTranslationProxy(tFn, key)
    },
  })
}

export function useI18n() {
  const { t: tFn, i18n } = useTranslation()

  const t = useMemo(() => createTranslationProxy(tFn as (key: string) => string), [tFn])

  const locale = i18n.language
  const setLocale = useCallback(
    (lng: string) => {
      void i18n.changeLanguage(lng)
    },
    [i18n],
  )

  return { t, locale, setLocale }
}
